<script setup lang="ts">
import { money } from '@nuts/auto-delivery-sdk/dist/utils/format';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import ThemedButton from '@/components/base/ThemedButton.vue';
import ApplePayButton from '@/components/cart/ApplePayButton.vue';
import ExpressCheckout from '@/components/cart/ExpressCheckout.vue';
import { ExpressLocation } from '@/composables/useExpressCheckout';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useProceedToCheckout } from '@/composables/useProceedToCheckout';
import { Money } from '@/utils/money';

defineProps<{
  hideExpressCheckout?: boolean;
  totalCost?: Money;
}>();

const route = useRoute();

const { flags } = useFeatureFlags();
const { isPending, proceedToCheckout } = useProceedToCheckout();

const trackingEventLocation = computed<ExpressLocation>(() =>
  route.path === '/cart/added' ? 'CartAdded' : 'Cart',
);

const showExpressCheckout = computed(() => flags.expressCheckout && route.path === '/cart');
</script>

<template>
  <div data-test="cart-action-block">
    <ThemedButton
      v-if="flags.cartLayoutUpdates"
      class="w-full h-12 py-4 md:px-6 lg:px-8 md:text-sm lg:text-base"
      data-test="checkout-button"
      :disabled="isPending"
      @click.prevent="() => proceedToCheckout()"
    >
      <img
        v-if="isPending"
        class="w-4 h-4 mr-2 animate-spin"
        src="@/assets/spinner.svg"
        alt="Loading"
      />
      Proceed to Checkout
      <span v-if="totalCost" class="md:hidden">&nbsp;- {{ money(totalCost) }}</span>
    </ThemedButton>
    <button
      v-else
      class="button primary"
      data-test="checkout-button"
      :disabled="isPending"
      @click.prevent="() => proceedToCheckout()"
    >
      <img
        v-if="isPending"
        class="w-4 h-4 mr-2 animate-spin"
        src="@/assets/spinner.svg"
        alt="spinner"
      />
      Proceed to Checkout
    </button>
    <span v-if="!hideExpressCheckout">
      <ExpressCheckout v-if="showExpressCheckout" :trackingEventLocation />
      <ApplePayButton v-else :componentName="trackingEventLocation" />
    </span>
    <slot />
  </div>
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
